import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { newsAPI } from "../services/api";
import Pagination from "../components/common/Pagination";

// Utility function for truncating text
const truncateText = (text, maxLength) => {
  if (!text) return "";
  const plainText = text.replace(/<[^>]*>/g, "");
  return plainText.length > maxLength
    ? plainText.substring(0, maxLength) + "..."
    : plainText;
};

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await newsAPI.getAll({
          page: pagination.page,
          limit: pagination.limit,
        });
        setNews(response.data.data);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error("Failed to fetch news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [pagination.page]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLimitChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(e.target.value),
      page: 1,
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Helmet>
        <title>
          ข่าวประชาสัมพันธ์ | งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ
        </title>
        <meta
          name="description"
          content="ข่าวประชาสัมพันธ์และกิจกรรม งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 py-12">
        <div className="container-custom">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-4 bg-primary-100 rounded-full mb-4">
              <i className="fas fa-newspaper text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4 relative inline-block pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-secondary-500 after:rounded-full">
              ข่าวประชาสัมพันธ์
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              ข่าวสาร กิจกรรม และประกาศต่างๆ จากงานบริการวิชาการ
            </p>
          </div>

          {/* Results Info */}
          {!loading && news.length > 0 && (
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                พบทั้งหมด{" "}
                <span className="font-semibold text-primary-500">
                  {pagination.total}
                </span>{" "}
                รายการ
              </p>
              <select
                value={pagination.limit}
                onChange={handleLimitChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="10">10 รายการ/หน้า</option>
                <option value="20">20 รายการ/หน้า</option>
                <option value="30">30 รายการ/หน้า</option>
              </select>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="spinner"></div>
            </div>
          ) : news.length > 0 ? (
            <>
              {/* News Grid */}
              <div className="space-y-6">
                {news.map((item) => (
                  <article
                    key={item.news_id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      {item.main_image_url && (
                        <Link
                          to={`/news/${item.news_id}`}
                          className="md:w-72 h-48 md:h-auto overflow-hidden"
                        >
                          <img
                            src={item.main_image_url}
                            alt={item.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/images/no-image.png";
                            }}
                          />
                        </Link>
                      )}

                      {/* Content */}
                      <div className="flex-1 p-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-3 hover:text-primary-500 transition-colors">
                          <Link to={`/news/${item.news_id}`}>{item.title}</Link>
                        </h2>

                        {/* Meta Info */}
                        <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
                          <span className="flex items-center gap-1">
                            <i className="fas fa-calendar-alt text-primary-500"></i>
                            {formatDate(item.publish_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="fas fa-user text-primary-500"></i>
                            {item.author || "ไม่ระบุ"}
                          </span>
                          {item.image_count > 0 && (
                            <span className="flex items-center gap-1">
                              <i className="fas fa-images text-primary-500"></i>
                              {item.image_count} รูป
                            </span>
                          )}
                        </div>

                        {/* Excerpt */}
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {item.content
                            ? truncateText(item.content, 160)
                            : "ไม่มีรายละเอียด"}
                        </p>

                        {/* Read More */}
                        <Link
                          to={`/news/${item.news_id}`}
                          className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium transition-colors group"
                        >
                          อ่านเพิ่มเติม
                          <i className="fas fa-arrow-right text-sm group-hover:translate-x-1 transition-transform"></i>
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          ) : (
            // No Results
            <div className="bg-white max-w-md mx-auto text-center py-16">
              <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-newspaper text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                ไม่พบข่าวประชาสัมพันธ์
              </h3>
              <p className="text-gray-600">กรุณาตรวจสอบอีกครั้งในภายหลัง</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsPage;
